import React, { useContext, useEffect, useState } from 'react'
import ReactDOM from 'react-dom'
import { Button, ErrorBoundary, QuickView, ProductCardSkeleton } from '@sainsburys-tech/bolt'
import { ThemeContext } from 'styled-components'
import { useMedia } from '@sainsburys-tech/boltui-hooks'
import { Carousel, SliderWrapper, StyledProductCard, StyledProductCardAlt } from './Recommendations.styles'
import { cleanString, fetcher } from '../../../helpers/ComponentHelper'
import { hotspotClickTracking, quickviewTracking, quickviewDetailsTracking } from '../hot-spot/hot-spot.tracking'

const Recommendations = ({ productSKU, allProducts, showLook, heading }) => {
  const [recommendedProducts, setRecommendedProducts] = useState([])
  const [showQuickView, toggleQuickView] = useState(false)
  const [lookProducts, setLookProducts] = useState([])
  const [selectedProductID, setSelectedProduct] = useState(productSKU)
  const [rxsLoading, setRxsLoading] = useState(true)
  const isMobile = useMedia([false, true], false, [`(min-width: 576px)`, `(max-width: 576px)`])
  const theme = useContext(ThemeContext)

  const brandParams = {
    argos: {
      accountId: '9131',
      sig: '5fff26234bfaae662930581f'
    },
    habitat: {
      accountId: '9251',
      sig: '608bd79f2f21645d9c4f83be'
    }
  }

  const transformSelectedProduct = () => {
    const selectedProduct = lookProducts[productSKU]

    return {
      productName: selectedProduct?.data.attributes.name,
      sku: selectedProduct?.data.id,
      price: selectedProduct?.included.find((included) => included.type === 'prices')?.attributes.now,
      isHighlighted: true
    }
  }
  const getRecommendations = async () => {
    const { accountId, sig } = brandParams[theme.channel] || brandParams.argos
    const url = `https://cdn.syteapi.com/v1.1/similars?q=sku:${productSKU}&account_id=${accountId}&sig=${sig}&fields=*&features=similars&syte_product=similars&imageUrl=https://media.4rgos.it/i/Argos/${productSKU}_R_Z001A`
    const similarProducts = []
    const productList = []

    try {
      const { json, ok } = await fetcher(url)

      if (ok) {
        const { response } = json

        response.forEach((product) => {
          similarProducts.push({
            productName: product.description,
            sku: product.sku,
            price: product.floatPrice
          })
          productList.push({ id: product.sku, hotspotProductType: 'recommended' })
        })
      }
      const displayedList = [{ id: productSKU, hotspotProductType: 'main' }, ...productList.slice(0, 8)]

      return ReactDOM.unstable_batchedUpdates(() => {
        setRxsLoading(false)
        setRecommendedProducts([transformSelectedProduct(), ...similarProducts.slice(0, 8)])
        hotspotClickTracking(productSKU, heading, displayedList)
      })
    } catch {
      return ReactDOM.unstable_batchedUpdates(() => {
        setRxsLoading(false)
        setRecommendedProducts([transformSelectedProduct()])
        hotspotClickTracking(productSKU, heading, [{ id: productSKU, hotspotProductType: 'main' }])
      })
    }
  }

  useEffect(() => {
    if (productSKU) {
      getRecommendations()
    }
  }, [productSKU])

  const handleQuickViewToggle = (productData, productType) => {
    setSelectedProduct(productData?.sku || productSKU)
    toggleQuickView(!showQuickView)

    if (!showQuickView)
      quickviewTracking(
        'moredetails',
        [{ id: productData.sku || productSKU, hotspotProductType: productType }],
        heading
      )
  }

  const handleProductCardClick = (e, data, productType) => {
    e.preventDefault()
    handleQuickViewToggle(data, productType)
    return false
  }

  const trackingOptions = {
    clickOrigin: 'hotspotRecommendations',
    queryParams: {
      hotspotTitle: cleanString(heading),
      hotspotProductType: showLook || allProducts.includes(selectedProductID) ? 'main' : 'recommended'
    }
  }

  const quickViewProduct = [
    { id: selectedProductID, hotspotProductType: allProducts.includes(selectedProductID) ? 'main' : 'recommended' }
  ]

  const showRecommendations = () => {
    const dataTest = 'm038-similar-product'
    return (
      <SliderWrapper data-test='m038-similar-products'>
        <Carousel numberOfProducts={recommendedProducts.length}>
          {rxsLoading &&
            [...Array(4)].map((_, index) => {
              return (
                <div key={`skeleton-${index}`}>
                  <ProductCardSkeleton />
                </div>
              )
            })}
          {recommendedProducts.map((product, index) => (
            <StyledProductCard
              key={`product-card-${index}-${product.sku}`}
              data-test={`${dataTest}-${product.sku}`}
              isHighlighted={product.isHighlighted}
              productLinkOverride='#'
              onClick={(e) => handleProductCardClick(e, product, product.isHighlighted ? 'main' : 'recommended')}
              partNumber={product.sku}
              mobileLayout='vertical'
              name={product.productName}
              price={product.price}
              cta={
                <Button
                  data-test={`${dataTest}-${index}-button`}
                  kind={product.isHighlighted ? 'primary' : 'secondary'}
                  onClick={() => handleQuickViewToggle(product, product.isHighlighted ? 'main' : 'recommended')}
                  full
                >
                  More Details
                </Button>
              }
            />
          ))}
        </Carousel>
      </SliderWrapper>
    )
  }

  return (
    <ErrorBoundary>
      {showLook || !recommendedProducts.length ? (
        <SliderWrapper data-test='m038-look-products'>
          <Carousel numberOfProducts={allProducts.length}>
            <QuickView.ProductFetch
              fetch={showLook}
              partNumbers={allProducts}
              render={({ products }) => {
                setLookProducts(products)
                return Object.entries(products).map((product, index) => {
                  const productAttributes = product[1].data.attributes
                  const price = product[1].included.find((included) => included.type === 'prices')?.attributes
                  const productData = {
                    sku: product[0],
                    productName: productAttributes.name,
                    price: price.now
                  }
                  return (
                    <StyledProductCardAlt
                      key={`product-card-${index}`}
                      data-test={`m038-look-product-${index}`}
                      partNumber={product[0]}
                      productLinkOverride='#'
                      name={productAttributes.name}
                      mobileLayout='vertical'
                      price={price.now}
                      onClick={(e) => handleProductCardClick(e, productData, 'main')}
                      cta={
                        <Button
                          kind='secondary'
                          onClick={() => handleQuickViewToggle(productData, 'main')}
                          full
                          data-test={`m038-look-product-more-details-${index}`}
                        >
                          More Details
                        </Button>
                      }
                    />
                  )
                })
              }}
            />
          </Carousel>
        </SliderWrapper>
      ) : (
        showRecommendations()
      )}
      {selectedProductID && showQuickView && (
        <QuickView
          overrideMaxWidth={isMobile ? '80%' : '50%'}
          show={showQuickView}
          toggleQuickView={handleQuickViewToggle}
          selectedProductId={selectedProductID}
          heading='A closer look'
          data-test='m038-quickview'
          onFullDetailsClick={() => quickviewDetailsTracking('hotspotLb_fullDetails', heading, quickViewProduct)}
          onSlideChanged={(newIndex, totalImages) =>
            quickviewDetailsTracking('hotspotLb_chevron', heading, quickViewProduct, `${newIndex} of ${totalImages}`)
          }
          trackingOptions={trackingOptions}
        />
      )}
    </ErrorBoundary>
  )
}

export default Recommendations
